import { createComponentById, NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import { HtmlId, OfferSummaryCardHtmlId } from '../enums/elementId';
import { HomesPassedType } from '../enums/product';
import { createOfferSummaryCard, OfferSummaryCard } from '@vfde-brix/ws10/offer-summary-card';
import { State } from 'Container/PriceSection';
import { mountFootnoteLinks } from './footnoteLink';
import { JarvisProduct, ProductOption } from '@vfde-sails/avc-core';
import { getPriceLegalText, getPriceSubline } from '../util/components/textHelper';
import {
    getAdditionalPricesProps,
    getHeadlineProps,
    getPromoPricesProps,
} from '../util/components/offerSummaryCardHelper';
import { mountPriceInstallationTextTooltip, mountPriceSublineTooltip } from './tooltip';
import { getUpdatedPriceButtonProps } from '../util/components/buttonHelper';
import { updateFloatingButton } from './button';
import { Button } from '@vfde-brix/ws10/button';

export const mountOfferSummaryCard = (id: HtmlId) =>
    createComponentById(createOfferSummaryCard, id, NO_PATTERN_BUSINESS_LOGIC);

const updatePriceHeadline = (
    offerSummaryCard: OfferSummaryCard,
    tariffs: JarvisProduct[],
    isPreFiber: boolean,
    isRegularFiber: boolean,
    tariffName?: string,
    homesPassedType?: HomesPassedType,
) => {
    offerSummaryCard.updateHeadline(getHeadlineProps(
        isPreFiber,
        isRegularFiber,
        tariffName,
        homesPassedType
    ));

    mountFootnoteLinks(tariffs, isPreFiber);
};

const updatePriceSubline = (
    offerSummaryCard: OfferSummaryCard,
    product: JarvisProduct | undefined,
    isRegularFiber: boolean,
    isContentEnabled: boolean,
    homesPassed?: HomesPassedType,
) => {
    offerSummaryCard.updateSubline([
        getPriceSubline(
            product,
            isRegularFiber,
            isContentEnabled,
            homesPassed,
        ),
    ]);

    isContentEnabled && mountPriceSublineTooltip(OfferSummaryCardHtmlId.TARIFF_SUBLINE_TOOLTIP);
};

export const updatePriceLegalText = (
    offerSummaryCard: OfferSummaryCard,
    product: JarvisProduct,
    installationFee?: ProductOption,
) => {
    offerSummaryCard.updateLegalText(getPriceLegalText(product));

    installationFee && mountPriceInstallationTextTooltip(
        OfferSummaryCardHtmlId.INSTALLATION_FEE_TEXT_TOOLTIP,
        +installationFee.price.monthly
    );
};

export const updateOfferSummaryCard = (
    state: State,
    offerSummaryCard: OfferSummaryCard | null,
    btnOnClick: () => void,
    floatingBtn?: Button,
) => {
    if (!offerSummaryCard) {
        return;
    }

    const {
        selectedTariff,
        isContentEnabled,
        homesPassed,
        tariffs,
        avcResult,
        installationFee,
        recommendedTariff,
    } = state;

    const { id, name } = selectedTariff || {};
    const isFiberAvailable = avcResult?.availability.fiber;
    const isPreFiber = (isFiberAvailable || isFiberAvailable === undefined) && isContentEnabled;
    const isRegularFiber = Boolean(isFiberAvailable && !isContentEnabled);
    const isRecommendedTariff = recommendedTariff && recommendedTariff.id === selectedTariff?.id;

    updatePriceHeadline(
        offerSummaryCard,
        tariffs,
        isPreFiber,
        isRegularFiber,
        name,
        homesPassed
    );

    updatePriceSubline(
        offerSummaryCard,
        selectedTariff,
        isRegularFiber,
        isContentEnabled,
        homesPassed,
    );

    offerSummaryCard.updateButtons([
        getUpdatedPriceButtonProps(
            id,
            avcResult,
            btnOnClick,
            isPreFiber,
            homesPassed
        ),
    ]);

    if (isContentEnabled && selectedTariff) {
        offerSummaryCard.updatePrices(
            getPromoPricesProps(selectedTariff),
            getAdditionalPricesProps(selectedTariff),
        );

        updatePriceLegalText(offerSummaryCard, selectedTariff, installationFee);
    }

    offerSummaryCard.toggleHighlightBadgeContainer(isContentEnabled && isRecommendedTariff);
    offerSummaryCard.toggleMiddleContentContainer(isContentEnabled);
    offerSummaryCard.toggleAdditionalTextContainer(isContentEnabled);

    updateFloatingButton(
        floatingBtn,
        floatingBtn?.getProperties(),
        selectedTariff?.id,
        avcResult,
        btnOnClick,
        isPreFiber
    );
};
